import anime from 'animejs/lib/anime.es.js';
import { animateStagger } from './interaction.js';
import { getMapManager } from '../components/Map/MapManager.js';
import { launchNiraViewer, NiraViewpoints } from './niraExample.js';
import { getNiraManager } from './niraManager.js';

// Helper function to create a metric block
function createMetricBlock(data) {
    const block = document.createElement('div');
    block.className = 'block';
    block.style.width = data.width;
    
    block.innerHTML = `
        <div class="block-wrapper">
            <div class="block-body">
                <div class="block-value">${data.value}</div>
                <div class="block-label">${data.label}</div>
            </div>
        </div>
    `;
    
    return block;
}

// Function to clear any existing data panels
function clearDataPanels() {
    const existingPanel = document.querySelector('.data-panel');
    if (existingPanel) {
        existingPanel.classList.remove('visible');
        // Remove after animation
        setTimeout(() => {
            existingPanel.remove();
        }, 300); // Match CSS transition duration
    }
}

// Function to create and show data panel
function showDataPanel(project) {
    // Always clear existing panels first
    clearDataPanels();
    
    console.log('[Debug] showDataPanel called with project:', project);
    
    // Check if project has data panels
    if (!project.data_panels || !project.data_panels.length) {
        console.log('[Debug] No data panels found:', {
            hasDataPanels: !!project.data_panels,
            dataPanelsLength: project.data_panels?.length,
            rawProject: JSON.stringify(project)
        });
        return;
    }

    // Filter enabled panels - Statamic replicator fields are enabled by default unless explicitly disabled
    const enabledPanels = project.data_panels.filter(panel => panel.enabled !== false);
    console.log('[Debug] Filtered enabled panels:', {
        total: project.data_panels.length,
        enabled: enabledPanels.length,
        panels: enabledPanels,
        firstPanel: project.data_panels[0],
        enabledState: project.data_panels[0]?.enabled
    });
    
    if (!enabledPanels.length) {
        console.log('[Debug] No enabled panels found');
        return;
    }

    // Create new data panel
    const dataPanel = document.createElement('div');
    dataPanel.className = 'data-panel';
    
    const wrapper = document.createElement('div');
    wrapper.className = 'data-wrapper';
    
    // Calculate widths based on number of panels
    const panelCount = enabledPanels.length;
    const width = panelCount === 1 ? '100%' : '50%';
    
    // Add metric blocks
    enabledPanels.forEach(panel => {
        console.log('[Debug] Processing panel:', panel);
        const blockData = {
            width: width,
            value: panel.project_data_value || panel.data_value,
            label: panel.project_data_label || panel.data_label
        };
        console.log('[Debug] Created block data:', blockData);
        wrapper.appendChild(createMetricBlock(blockData));
    });
    
    dataPanel.appendChild(wrapper);
    document.body.appendChild(dataPanel);
    
    // Trigger animation after a frame
    requestAnimationFrame(() => {
        dataPanel.classList.add('visible');
    });
}

export function initIntro(onIntroComplete) {
    const mapManager = getMapManager();
    
    // Get DOM elements - already in the template
    const button = document.querySelector('.button');
    const intro = document.querySelector('.intro');
    const scene = document.getElementById('scene');
    const panels = scene.querySelectorAll('.panel');

    // We'll set up hover events after the intro transition
    const setupPanelHoverEvents = () => {
        panels.forEach(panel => {
            panel.addEventListener('mouseenter', () => {
                const lat = parseFloat(panel.getAttribute('data-lat'));
                const lng = parseFloat(panel.getAttribute('data-lng'));
                if (lat && lng) {
                    mapManager.showPulsingMarker(lng, lat);
                }
            });
    
            panel.addEventListener('mouseleave', () => {
                mapManager.resetMarkers();
            });
        });
    };

    // Handle explore button click
    button.addEventListener('click', () => {
        console.log("[Intro] Explore button clicked");

        // Check if map is ready
        if (!mapManager.getMap() || !mapManager.getMap().loaded()) {
            console.log("[Intro] Map not ready yet, retrying in 500ms...");
            
            // Add loading state to button
            button.style.opacity = '0.7';
            button.style.cursor = 'wait';
            const originalText = button.textContent;
            button.textContent = 'Loading...';
            
            let retryCount = 0;
            const maxRetries = 20; // 10 seconds max
            const retryInterval = setInterval(() => {
                retryCount++;
                if (mapManager.getMap() && mapManager.getMap().loaded()) {
                    clearInterval(retryInterval);
                    console.log("[Intro] Map ready, starting transition");
                    // Reset button state before transition
                    button.style.opacity = '';
                    button.style.cursor = '';
                    button.textContent = originalText;
                    mapManager.transitionFromIntro();
                } else if (retryCount >= maxRetries) {
                    clearInterval(retryInterval);
                    console.error("[Intro] Map failed to load after 10 seconds");
                    // Reset button state on failure
                    button.style.opacity = '';
                    button.style.cursor = '';
                    button.textContent = originalText;
                }
            }, 500);

            // Clean up interval if user navigates away
            window.addEventListener('beforeunload', () => {
                clearInterval(retryInterval);
                // Reset button state
                button.style.opacity = '';
                button.style.cursor = '';
                button.textContent = originalText;
            }, { once: true });
            return;
        }

        // Initial map transition
        mapManager.transitionFromIntro();

        // Add fade-out class to trigger crescent animation
        const introContainer = document.getElementById('intro');
        introContainer.classList.add('fade-out');

        // Fade out intro content after text effect
        setTimeout(() => {
            anime({
                targets: [intro, '#intro::before'],
                opacity: 0,
                duration: 500,
                easing: 'easeOutQuad',
                complete: function() {
                    // Keep intro container but remove content
                    intro.remove();
                    
                    // Show scene and start panel animation
                    scene.classList.add('visible');
                    const panelsContainer = scene.querySelector('.panels.stagger');
                    if (panelsContainer) {
                        animateStagger(panelsContainer, {
                            duration: 500,
                            delay: 100,
                            startDelay: 0,
                            complete: () => {
                                console.log('[Intro] Panel animation complete, setting up hover events');
                                setupPanelHoverEvents();
                            }
                        });
                    }
                }
            });
        }, 500);
    });
    
    // Handle panel clicks
    panels.forEach(panel => {
        panel.addEventListener('click', () => {
            const lat = parseFloat(panel.getAttribute('data-lat'));
            const lng = parseFloat(panel.getAttribute('data-lng'));
            const cityId = panel.getAttribute('data-city-id');
            const projectsData = panel.getAttribute('data-projects');
            
            if (!lat || !lng || !cityId) {
                console.error("[Intro] Missing location data for panel");
                return;
            }
            
            console.log(`[Intro] Panel clicked with location: ${lat}, ${lng} and city ID: ${cityId}`);
            console.log('[Intro] Panel projects data:', projectsData);
            
            // Get all panels data for navigation
            const allPanels = Array.from(panels).map(p => ({
                id: p.getAttribute('data-city-id'),
                title: p.querySelector('.panel-content h3').textContent,
                icon: p.querySelector('.panel-content img').getAttribute('src'),
                description: p.querySelector('.panel-content p').textContent,
                summary: p.querySelector('.panel-content').getAttribute('data-summary'),
                lat: parseFloat(p.getAttribute('data-lat')),
                lng: parseFloat(p.getAttribute('data-lng')),
                projects: p.getAttribute('data-projects')
            }));
            
            // Get current panel data
            const currentPanel = panel.querySelector('.panel-content');
            const currentTitle = currentPanel.querySelector('h3').textContent;
            const currentIcon = currentPanel.querySelector('img').getAttribute('src');
            const currentDesc = currentPanel.querySelector('p').textContent;
            const currentSummary = currentPanel.getAttribute('data-summary');
            
            // Update info container and location panel with content
            const infoContainer = document.querySelector('.info-container');
            const locationPanel = document.getElementById('location-panel');
            const projectsContainer = document.querySelector('.projects-container');
            
            if (infoContainer && locationPanel && projectsContainer) {
                // Update location panel
                const wrapper = document.createElement('div');
                wrapper.className = 'panel-wrap';
                
                // Create header
                const header = document.createElement('div');
                header.className = 'panel-header';
                header.innerHTML = `
                    <img src="${currentIcon}" alt="${currentTitle} Icon">
                    <h3>${currentTitle}</h3>
                `;
                
                // Create body with description and summary
                const body = document.createElement('div');
                body.className = 'panel-body';
                body.innerHTML = `
                    <p class="panel-description">${currentDesc}</p>
                    <div class="panel-summary">
                        ${currentSummary}
                    </div>
                `;
                
                wrapper.appendChild(header);
                wrapper.appendChild(body);
                locationPanel.innerHTML = '';
                locationPanel.appendChild(wrapper);

                // Create project panels
                projectsContainer.innerHTML = '';
                try {
                    console.log('[Debug] Raw projects data:', projectsData);
                    const projects = JSON.parse(projectsData || '[]');
                    console.log('[Debug] Parsed projects data:', {
                        projects,
                        firstProject: projects[0],
                        firstProjectDataPanels: projects[0]?.data_panels
                    });
                    projects.forEach(project => {
                        // Check if project has required fields
                        if (project.project_name && project.project_description) {
                            const projectPanel = document.createElement('div');
                            projectPanel.className = 'project-panel';
                            projectPanel.innerHTML = `
                                <div class="project-wrap">
                                    <div class="project-header">
                                        <h4>${project.project_name}</h4>
                                    </div>
                                    <div class="project-body">
                                        <div class="project-summary">
                                            ${project.project_description}
                                        </div>
                                        ${project.launch_nira ? `
                                            <div class="project-actions">
                                                <button class="launch-nira-btn">Launch Nira Viewer</button>
                                            </div>
                                        ` : ''}
                                    </div>
                                </div>
                            `;

                            // Store project coordinates for hover effect
                            projectPanel.dataset.lat = project.latitude;
                            projectPanel.dataset.lng = project.longitude;

                            // Add hover events to project panel
                            projectPanel.addEventListener('mouseenter', () => {
                                console.log('[Debug] Project panel mouseenter:', {
                                    name: project.project_name,
                                    lat: projectPanel.dataset.lat,
                                    lng: projectPanel.dataset.lng,
                                    element: projectPanel
                                });
                                const lat = parseFloat(projectPanel.dataset.lat);
                                const lng = parseFloat(projectPanel.dataset.lng);
                                if (lat && lng) {
                                    // Find the corresponding project marker
                                    const projectMarker = mapManager.projectMarkers.find(marker => {
                                        const [markerLng, markerLat] = marker.getLngLat().toArray();
                                        return markerLat === lat && markerLng === lng;
                                    });
                                    
                                    if (projectMarker) {
                                        mapManager.showPulsingProjectMarker(projectMarker);
                                    } else {
                                        console.warn('[Debug] Could not find project marker:', {
                                            lat, lng, dataset: projectPanel.dataset
                                        });
                                    }
                                }
                            });

                            projectPanel.addEventListener('mouseleave', () => {
                                console.log('[Debug] Project panel mouseleave:', {
                                    name: project.project_name
                                });
                                mapManager.resetProjectMarkers();
                            });

                            // Add click handler for project focus
                            // Add click handler for project focus
                            projectPanel.addEventListener('click', () => {
                                console.log('[Debug] Project panel clicked - Full project data:', project);
                                console.log('[Debug] Project panel clicked - Data panels:', {
                                    name: project.project_name,
                                    lat: project.latitude,
                                    lng: project.longitude,
                                    dataPanels: project.data_panels,
                                    rawDataPanels: JSON.stringify(project.data_panels)
                                });
                                mapManager.flyToProject(project.latitude, project.longitude);
                                showDataPanel(project);
                            });

                            // Add Nira launch handler if enabled
                            if (project.launch_nira) {
                                const launchBtn = projectPanel.querySelector('.launch-nira-btn');
                                if (launchBtn) {
                                    launchBtn.addEventListener('click', (e) => {
                                        e.stopPropagation(); // Prevent panel click
                                        console.log('[Debug] Launching Nira viewer for:', project.project_name);
                                        // Use the consistent Nira launch method
                                        const niraManager = getNiraManager();
                                        if (niraManager) {
                                            niraManager.launchWithPosition(NiraViewpoints.prophetsMosque);
                                        } else {
                                            console.error('[Intro] Nira manager not initialized');
                                        }
                                    });
                                }
                            }

                            projectsContainer.appendChild(projectPanel);
                        }
                    });
                } catch (error) {
                    console.error('[Intro] Error parsing projects:', error);
                }

                // Show the info container
                infoContainer.classList.add('visible');
            }

            // Update bottom navigation
            const bottomNav = document.querySelector('.bottom-nav');
            if (bottomNav) {
                const navItems = bottomNav.querySelectorAll('.nav-item');
                navItems.forEach(navItem => {
                    const cityId = navItem.getAttribute('data-location-id');
                    const panelData = allPanels.find(p => p.id === cityId);
                    
                    if (panelData) {
                        navItem.addEventListener('click', () => {
                            console.log('[Intro] Navigation clicked for city:', cityId);
                            
                            // Update location panel content
                            if (locationPanel) {
                                const header = locationPanel.querySelector('.panel-header');
                                const body = locationPanel.querySelector('.panel-body');
                                
                                if (header && body) {
                                    header.innerHTML = `
                                        <img src="${panelData.icon}" alt="${panelData.title} Icon">
                                        <h3>${panelData.title}</h3>
                                    `;
                                    body.innerHTML = `
                                        <p class="panel-description">${panelData.description}</p>
                                        <div class="panel-summary">
                                            ${panelData.summary}
                                        </div>
                                    `;
                                }
                            }

                            // Update projects
                            if (projectsContainer) {
                                projectsContainer.innerHTML = '';
                                try {
                                    const projects = JSON.parse(panelData.projects || '[]');
                                    console.log('[Intro] Creating project panels for navigation:', projects);
                                    projects.forEach(project => {
                                        // Check if project has required fields
                                        if (project.project_name && project.project_description) {
                                            const projectPanel = document.createElement('div');
                                            projectPanel.className = 'project-panel';
                                            projectPanel.innerHTML = `
                                                <div class="project-wrap">
                                                    <div class="project-header">
                                                        <h4>${project.project_name}</h4>
                                                    </div>
                                                    <div class="project-body">
                                                        <div class="project-summary">
                                                            ${project.project_description}
                                                        </div>
                                                        ${project.launch_nira ? `
                                                            <div class="project-actions">
                                                                <button class="launch-nira-btn">Launch Nira Viewer</button>
                                                            </div>
                                                        ` : ''}
                                                    </div>
                                                </div>
                                            `;

                                            // Store project coordinates for hover effect
                                            projectPanel.dataset.lat = project.latitude;
                                            projectPanel.dataset.lng = project.longitude;

                                            // Add hover events to project panel
                                            projectPanel.addEventListener('mouseenter', () => {
                                                console.log('[Debug] Project panel mouseenter (nav):', {
                                                    name: project.project_name,
                                                    lat: projectPanel.dataset.lat,
                                                    lng: projectPanel.dataset.lng,
                                                    element: projectPanel
                                                });
                                                const lat = parseFloat(projectPanel.dataset.lat);
                                                const lng = parseFloat(projectPanel.dataset.lng);
                                                if (lat && lng) {
                                                    // Find the corresponding project marker
                                                    const projectMarker = mapManager.projectMarkers.find(marker => {
                                                        const [markerLng, markerLat] = marker.getLngLat().toArray();
                                                        return markerLat === lat && markerLng === lng;
                                                    });
                                                    
                                                    if (projectMarker) {
                                                        mapManager.showPulsingProjectMarker(projectMarker);
                                                    } else {
                                                        console.warn('[Debug] Could not find project marker:', {
                                                            lat, lng, dataset: projectPanel.dataset
                                                        });
                                                    }
                                                }
                                            });

                                            projectPanel.addEventListener('mouseleave', () => {
                                                console.log('[Debug] Project panel mouseleave (nav):', {
                                                    name: project.project_name
                                                });
                                                mapManager.resetProjectMarkers();
                                            });

                                            // Add click handler for project focus
                                            projectPanel.addEventListener('click', () => {
                                                console.log('[Debug] Project panel clicked (nav):', {
                                                    name: project.project_name,
                                                    lat: project.latitude,
                                                    lng: project.longitude,
                                                    dataPanels: project.data_panels
                                                });
                                                mapManager.flyToProject(project.latitude, project.longitude);
                                                showDataPanel(project);
                                            });

                                            // Add Nira launch handler if enabled
                                            if (project.launch_nira) {
                                                const launchBtn = projectPanel.querySelector('.launch-nira-btn');
                                                if (launchBtn) {
                                                    launchBtn.addEventListener('click', (e) => {
                                                        e.stopPropagation(); // Prevent panel click
                                                        console.log('[Debug] Launching Nira viewer for:', project.project_name);
                                                        // Use the consistent Nira launch method
                                        const niraManager = getNiraManager();
                                        if (niraManager) {
                                            niraManager.launchWithPosition(NiraViewpoints.prophetsMosque);
                                        } else {
                                            console.error('[Intro] Nira manager not initialized');
                                        }
                                                    });
                                                }
                                            }

                                            projectsContainer.appendChild(projectPanel);
                                        }
                                    });
                                } catch (error) {
                                    console.error('[Intro] Error parsing projects:', error);
                                }
                            }

                            // Clear any existing data panels before transitioning
                            clearDataPanels();
                            
                            // Transition map to new location with projects
                            mapManager.transitionToLocation(
                                panelData.lat,
                                panelData.lng,
                                panelData.id,
                                JSON.parse(panelData.projects || '[]')
                            );
                        });
                    }
                });
            }
            
            // Show UI and location panel
            showMainUi();
            if (locationPanel) {
                locationPanel.classList.add('visible');
            }
            
            // Transition map to location with projects
            try {
                const projects = JSON.parse(projectsData || '[]');
                mapManager.transitionToLocation(lat, lng, cityId, projects);
            } catch (error) {
                console.error('[Intro] Error parsing projects data:', error);
                mapManager.transitionToLocation(lat, lng, cityId, []);
            }
            
            // Fade out scene
            anime({
                targets: scene,
                opacity: 0,
                duration: 500,
                easing: 'easeOutQuad',
                complete: () => {
                    // Remove scene and intro container
                    scene.remove();
                    const introContainer = document.getElementById('intro');
                    if (introContainer) {
                        console.log("[Intro] Removing intro container");
                        introContainer.remove();
                    }
                    
                    if (typeof onIntroComplete === 'function') {
                        onIntroComplete();
                    }
                }
            });
        });
    });
}

function showMainUi() {
    const info = document.getElementById('info');
    const queryContainer = document.getElementById('query-container');
    const bottomNav = document.querySelector('.bottom-nav');
    
    if (info) info.classList.remove('hidden');
    if (queryContainer) queryContainer.classList.remove('hidden');
    if (bottomNav) bottomNav.classList.add('visible');
}